import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { checkWhatsAppConfig } from '../../utils/whatsappHelper';

const WhatsAppTemplates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    category: 'UTILITY',
    language: 'en_US',
    headerType: 'NONE',
    headerText: '',
    bodyText: '',
    footerText: '',
    buttons: []
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  // Helper to get user data from context or localStorage
  const getUserData = () => {
    if (user?.tenantId && user?._id) return user;
    const storedUserData = localStorage.getItem('userData');
    return storedUserData ? JSON.parse(storedUserData) : null;
  };

  useEffect(() => {
    const checkConfigAndFetch = async () => {
      const userData = getUserData();
      if (!userData?.tenantId || !userData?.userid && !userData?._id) {
        setLoading(false);
        setIsConfigured(false);
        return;
      }
      
      const configured = await checkWhatsAppConfig(userData.tenantId, userData.userid || userData._id);
      setIsConfigured(configured);
      
      if (configured) {
        fetchTemplates();
        fetchMetadata();
      } else {
        setLoading(false);
      }
    };
    
    checkConfigAndFetch();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const userData = getUserData();
      if (!userData?.tenantId || !userData?._id) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/whatsapp-templates`, {
        headers: {
          tenantid: userData.tenantId,
          userid: userData._id || userData.userid
        }
      });

      if (response.data.status === 'success') {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      if (error.response?.status === 404) {
        // Config doesn't exist
        setIsConfigured(false);
      } else {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Error loading templates'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [langResponse, catResponse] = await Promise.all([
        axios.get(`${API_URL}/api/whatsapp-templates/meta/languages`),
        axios.get(`${API_URL}/api/whatsapp-templates/meta/categories`)
      ]);

      setLanguages(langResponse.data.data || []);
      setCategories(catResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.bodyText) {
      setMessage({
        type: 'error',
        text: 'Template name and body text are required'
      });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Build template structure for WhatsApp API
      const components = [];

      // Add header if present
      if (formData.headerType !== 'NONE') {
        components.push({
          type: 'HEADER',
          format: formData.headerType,
          ...(formData.headerType === 'TEXT' && { text: formData.headerText })
        });
      }

      // Add body (required)
      components.push({
        type: 'BODY',
        text: formData.bodyText
      });

      // Add footer if present
      if (formData.footerText) {
        components.push({
          type: 'FOOTER',
          text: formData.footerText
        });
      }

      // Add buttons if present
      if (formData.buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: formData.buttons
        });
      }

      const template = {
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
        language: formData.language,
        category: formData.category,
        components: components
      };

      const response = await axios.post(
        `${API_URL}/api/whatsapp-templates`,
        { template },
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'Template created successfully! It may take a few minutes to be approved.'
        });
        setShowCreateModal(false);
        resetForm();
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error creating template'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateName) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_URL}/api/whatsapp-templates/${templateName}`,
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'Template deleted successfully'
        });
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting template'
      });
    } finally {
      setLoading(false);
    }
  };

  const addButton = (type) => {
    const newButton = {
      type: type,
      text: ''
    };

    if (type === 'URL') {
      newButton.url = '';
    } else if (type === 'PHONE_NUMBER') {
      newButton.phone_number = '';
    }

    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, newButton]
    }));
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const updateButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) => 
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'UTILITY',
      language: 'en_US',
      headerType: 'NONE',
      headerText: '',
      bodyText: '',
      footerText: '',
      buttons: []
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      APPROVED: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      REJECTED: 'bg-red-100 text-red-800',
      DISABLED: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.PENDING}`}>
        {status}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      MARKETING: 'bg-purple-100 text-purple-800',
      UTILITY: 'bg-blue-100 text-blue-800',
      AUTHENTICATION: 'bg-indigo-100 text-indigo-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${categoryColors[category] || categoryColors.UTILITY}`}>
        {category}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Not configured state
  if (isConfigured === false) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="flex items-center justify-center w-16 h-16 mb-4 bg-amber-100 dark:bg-amber-900 rounded-full">
              <AlertCircle size={32} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
              WhatsApp No Configurado
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-6 max-w-md">
              Necesitas configurar WhatsApp antes de crear plantillas. Por favor, ve a la configuración de WhatsApp primero.
            </p>
            <button
              onClick={() => navigate('/settings')}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Ir a Configuración de WhatsApp
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">WhatsApp Message Templates</h2>
            <p className="text-gray-600">
              Manage your WhatsApp Business message templates
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Template
          </button>
        </div>

        {/* Alert Messages */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-600 mb-4">Create your first WhatsApp message template to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                    <div className="flex gap-2 mb-2">
                      {getCategoryBadge(template.category)}
                      {getStatusBadge(template.status)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(template.name)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete template"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Language:</span>
                    <span className="ml-2 text-gray-900">{template.language}</span>
                  </div>

                  {template.components && template.components.map((component, idx) => (
                    <div key={idx}>
                      {component.type === 'BODY' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500 mb-1">Body:</p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{component.text}</p>
                        </div>
                      )}
                      {component.type === 'HEADER' && (
                        <div className="mt-2 p-2 bg-blue-50 rounded">
                          <p className="text-xs text-gray-500 mb-1">Header:</p>
                          <p className="text-sm text-gray-800">{component.text || `[${component.format}]`}</p>
                        </div>
                      )}
                      {component.type === 'FOOTER' && (
                        <div className="mt-2 p-2 bg-gray-100 rounded">
                          <p className="text-xs text-gray-500">Footer: {component.text}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setSelectedTemplate(template)}
                  className="mt-4 w-full px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium"
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Create New Template</h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateTemplate} className="space-y-4">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., order_confirmation"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Use lowercase letters, numbers, and underscores only</p>
                </div>

                {/* Category and Language */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language *
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {languages.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Header Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Header Type
                  </label>
                  <select
                    name="headerType"
                    value={formData.headerType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="NONE">None</option>
                    <option value="TEXT">Text</option>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="DOCUMENT">Document</option>
                  </select>
                </div>

                {/* Header Text */}
                {formData.headerType === 'TEXT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header Text
                    </label>
                    <input
                      type="text"
                      name="headerText"
                      value={formData.headerText}
                      onChange={handleInputChange}
                      placeholder="Enter header text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Body Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Body Text *
                  </label>
                  <textarea
                    name="bodyText"
                    value={formData.bodyText}
                    onChange={handleInputChange}
                    placeholder="Enter your message body. Use {{1}}, {{2}} for variables."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Use double curly braces for variables: {`{{1}}, {{2}}`}</p>
                </div>

                {/* Footer Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Footer Text (Optional)
                  </label>
                  <input
                    type="text"
                    name="footerText"
                    value={formData.footerText}
                    onChange={handleInputChange}
                    placeholder="Enter footer text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buttons (Optional)
                  </label>
                  {formData.buttons.map((button, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={button.text}
                        onChange={(e) => updateButton(index, 'text', e.target.value)}
                        placeholder="Button text"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      {button.type === 'URL' && (
                        <input
                          type="text"
                          value={button.url}
                          onChange={(e) => updateButton(index, 'url', e.target.value)}
                          placeholder="URL"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      )}
                      {button.type === 'PHONE_NUMBER' && (
                        <input
                          type="text"
                          value={button.phone_number}
                          onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                          placeholder="Phone number"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeButton(index)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => addButton('QUICK_REPLY')}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      + Quick Reply
                    </button>
                    <button
                      type="button"
                      onClick={() => addButton('URL')}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      + URL Button
                    </button>
                    <button
                      type="button"
                      onClick={() => addButton('PHONE_NUMBER')}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      + Call Button
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Creating...' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Template Details Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Template Details</h3>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">{selectedTemplate.name}</h4>
                  <div className="flex gap-2 mb-4">
                    {getCategoryBadge(selectedTemplate.category)}
                    {getStatusBadge(selectedTemplate.status)}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                    {JSON.stringify(selectedTemplate, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplates;
